/**
 * Types du moteur Klondike — aucun DOM, aucun Three.js : tout ce fichier (et
 * les autres modules de src/game/) doit rester testable en vitest pur, comme
 * les moteurs de dkg-farkle et dkg-battle.
 *
 * Choix de direction artistique (cahier des charges §5, option "sure") :
 * enseignes classiques ♠♥♦♣, illustrees dans le style parchemin/bois du
 * studio — pas d'enseignes latines reinventees pour la V1.
 */

export const SUITS = ["spades", "hearts", "diamonds", "clubs"] as const;
export type Suit = (typeof SUITS)[number];

/** 1 = As, 11 = Valet, 12 = Dame, 13 = Roi. */
export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
export const RANKS: readonly Rank[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

export interface Card {
  readonly suit: Suit;
  readonly rank: Rank;
  /** Face visible (true) ou dos visible (false). Mute par le moteur uniquement. */
  faceUp: boolean;
}

/** Identifiant stable d'une carte (52 valeurs uniques) : cle des meshes 3D cote rendu. */
export function cardId(card: Pick<Card, "suit" | "rank">): string {
  return `${card.suit}-${card.rank}`;
}

export function isRed(suit: Suit): boolean {
  return suit === "hearts" || suit === "diamonds";
}

/** Nombre de colonnes du tableau. */
export const TABLEAU_COUNT = 7;

/** Ordre fixe des 4 fondations a l'ecran (cahier des charges §4 : ♦ ♣ ♥ ♠). */
export const FOUNDATION_SUITS: readonly Suit[] = ["diamonds", "clubs", "hearts", "spades"];

/** Etat complet d'une partie. Le moteur le mute en interne mais n'expose que des copies. */
export interface GameState {
  /** Pioche, face cachee ; le sommet est la FIN du tableau. */
  stock: Card[];
  /** Defausse, face visible ; le sommet est la FIN du tableau. */
  waste: Card[];
  /** 4 fondations, indexees comme FOUNDATION_SUITS ; de l'As (debut) au Roi (fin). */
  foundations: [Card[], Card[], Card[], Card[]];
  /** 7 colonnes ; l'index 0 est le fond (cartes couvertes), la fin est le sommet jouable. */
  tableau: Card[][];
  /** Nombre de cartes retournees a chaque tirage de pioche (mode standard = 3). */
  drawCount: 1 | 3;
  /** Compteur de coups (chaque action de jeu : tirage, deplacement, recyclage). */
  moves: number;
  /** Graine de la donne : permet "Rejouer la meme donne". */
  seed: number;
}

/** Source d'un deplacement vers une fondation ou un tableau. */
export type MoveSource =
  | { type: "waste" }
  | { type: "tableau"; col: number; index: number }
  | { type: "foundation"; suit: Suit };
